<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="name"><b>{{ $t('fleet/member') }}</b> {{name}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="flex-column-responsive gap-1">
                    <!-- Photo -->
                    <div role="group" class="form-group p-3">
                        <GalleryPicker class="gallery-picker" v-model="products.photo"
                            :ratioWidth="ratioWidth"
                            :ratioHeight="ratioHeight"
                            @update:ratioWidth="ratioWidth = $event"
                            @update:ratioHeight="ratioHeight = $event"
                            v-bind:class="{'is-invalid': genericValidator(products.photo) === false || errorValidator('Photo') === false }" 
                            :max="1">
                        </GalleryPicker>                    
                    </div>
                    <div class="d-flex flex-wrap flex-grow-1">
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('positionOptional') }}</label>
                            <CInput type="number" v-model="products.position" :invalid-feedback="errorOutput['Position']"
                            :is-valid="errorValidator('Position')"></CInput>
                        </div>
                        <!-- Select Type -->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label>{{ $t('type') }}</label>
                            <select class="mb-3 form-control" v-model="products.type" name="type" id="type">
                                <option value="">{{ $t('filterByType') }}</option>
                                <option value="Rota>">{{ $t('fleet') }}</option>
                                <option value="Membro">{{ $t('team') }}</option>
                            </select>
                        </div>
                        <!-- Year-->
                        <div role="group" class="form-group col-12 col-lg-6">
                            <label class=""> {{ $t('year') }}</label>
                            <CInput type="text" v-model="products.year"></CInput>
                        </div>
                        <!-- Names -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('nameMandatory') }}</label>
                            <VueEditor v-model="products.names[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(products.names) == false || errorValidator('Names') }"></VueEditor>
                            <div v-if="errorOutput['Names']" class="invalid-feedback">{{errorOutput['Names']}}</div>
                        </div>

                        <div role="group" class="form-group col-12">
                            <label>{{ $t('info') }}</label>
                            <div v-for="(productInfo, index) in products.mainProductInfos" :key="index" class="mb-3 row">
                                <div class="d-flex mb-2 col-2">
                                    <CInput
                                        type="text"
                                        :placeholder="`${$t('title')}`"
                                        v-model="productInfo.keys[currentLanguage.key]"
                                        class="me-2"
                                        v-bind:class="{'is-invalid': genericValidator(productInfo.keys) == false || errorValidator('KEYS') }"
                                    />
                                </div>

                                <div class="d-flex mb-2 col-2">
                                    <CInput
                                        type="text"
                                        :placeholder="`${$t('value')}`"
                                        v-model="productInfo.values[currentLanguage.key]"
                                        class="me-2"
                                        v-bind:class="{'is-invalid': genericValidator(productInfo.values) == false || errorValidator('VALUES') }"
                                    />
                                </div>

                                <div class="d-flex justify-content-end">
                                    <CButton class="btn btn-danger" @click="removeProductInfo(index)">
                                        {{ $t('delete') }}
                                    </CButton>
                                </div>
                            </div>

                            <CButton color="primary" class="btn mx-3" @click="addProductInfo()">
                                {{ $t('addProductInfo') }}
                            </CButton>
                        </div>
                        <!-- Datasheet -->
                        <CustomPicker 
                            v-for="language in supportedLanguages" 
                            :key="`datasheet-${language.key}`" 
                            v-show="language.key == currentLanguage.key" 
                            :identifier="language.key" 
                            :title="$t('datasheet')" 
                            class="form-group col-12 col-lg-6" 
                            v-model="products.datasheets[language.key]" 
                            v-bind:class="{'is-invalid': errorValidator('Datasheets')}">
                        </CustomPicker>
                    </div>
                </div>
                <!-- Description -->
                <div role="group" class="form-group col-12">
                    <label class=""> {{ $t('descriptionMandatory') }}</label>
                    <VueEditor v-model="products.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(products.descriptions) == false || errorValidator('Descriptions') }"></VueEditor>
                    <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                </div>
                <!-- Action button + submitting -->  
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                </div>
                <!-- submitting -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import Product from "@/classes/Product";
import { mapState } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import CustomPicker from '@/controls/CustomPicker.vue'
import FormLoading from '@/controls/FormLoading.vue'
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { API_HELPER } from "@/helpers/api.js";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, VueEditor, CustomPicker, FormLoading },
    props:{    
        baseProducts:
        {
            type: Object,
            default: null,
        },
        baseProductsName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            products: new Product({}, {}, {}, {}, 0, 0, []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage: "",
            mainProductInfos: [],
            type: "",
            ratioWidth: 2, // default ratio width
            ratioHeight: 7,  // default ratio height
        };
    },
    mounted: function()
    {
        this.resetProducts();
    },
    computed:
    {
        supportedLanguages: function()
        {
            return CONSTANTS.supportedLanguages;
        },
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function() {
            let result = {};

            // Validate alerts
            if (this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput)) {
                result = this.alertOutput.reduce((accum, currentVal) => {
                    accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate multilanguage fields for all supported languages
            for (const lang of this.supportedLanguages) {
                const langKey = lang.key;

                if (!this.products.names[langKey] || !this.products.names[langKey].trim()) {
                    result['Names'] = this.$i18n.t("validateBothTranslations", { lang: langKey });
                }
                if (!this.products.descriptions[langKey] || !this.products.descriptions[langKey].trim()) {
                    result['Descriptions'] = this.$i18n.t("validateBothTranslations", { lang: langKey });
                }
            }

            // Validate mainProductInfos for both languages
            for (const [index, productInfo] of this.products.mainProductInfos.entries()) {
                for (const lang of this.supportedLanguages) {
                    const langKey = lang.key;

                    if (!productInfo.keys[langKey] || !productInfo.keys[langKey].trim()) {
                        result[`KEYS`] = this.$i18n.t("validateKeyForTranslation", { lang: langKey });
                    }
                    if (!productInfo.values[langKey] || !productInfo.values[langKey].trim()) {
                        result[`VALUES`] = this.$i18n.t("validateValueForTranslation", { lang: langKey });
                    }
                }
            }

            return result;
        },

        ...mapState('products', ['postProductOutput']),
        ...mapState('products', ['putProductOutput']),
        name: function()
        {
            return this.baseProducts || this.baseProductsName ? (this.baseProducts ? this.baseProducts.name : this.baseProductsName) : null;
        }
    },
    methods:{
        resetProducts() {
            if (!this.baseProducts) {
                const names = {};
                const descriptions = {};
                CONSTANTS.supportedLanguages.forEach(language => { 
                    names[language.key] = ""; 
                    descriptions[language.key] = ""; 
                });

                this.products = new Product(names, descriptions, {}, []);
            } else {
                this.products = deepClone(new Product, this.baseProducts);

                this.products.mainProductInfos = this.products.mainProductInfos.map(info => ({
                    ...info,
                    keys: info.keys || {},
                    values: info.values || {},
                }));

                CONSTANTS.supportedLanguages.forEach(language => {
                    this.products.mainProductInfos.forEach(info => {
                        this.$set(info.keys, language.key, info.keys[language.key] || '');
                        this.$set(info.values, language.key, info.values[language.key] || '');
                    });
                });

                this.products.photo = { url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.products.photoPath]) };
            }

            // Ensure Vue reactivity system tracks changes to mainProductInfos
            this.$set(this.products, 'mainProductInfos', [...this.products.mainProductInfos]);
        },

        addProductInfo() {
            if (!this.products.mainProductInfos) {
                this.products.mainProductInfos = [];
            }
            this.products.mainProductInfos.push({
                keys: {
                    pt: '',
                    en: ''
                },
                values: {
                    pt: '',
                    en: ''
                }
            });
            
            // Ensure Vue reactivity system tracks changes to mainProductInfos
            this.$set(this.products, 'mainProductInfos', [...this.products.mainProductInfos]);
        },
        removeProductInfo(index) {
            if (Array.isArray(this.products.mainProductInfos) && this.products.mainProductInfos.length > index) {
                this.products.mainProductInfos.splice(index, 1);
                
                // Ensure Vue reactivity system tracks changes to mainProductInfos
                this.$set(this.products, 'mainProductInfos', [...this.products.mainProductInfos]);
            }
        },
        submitClicked() {
            this.triedSubmit = true;

            if (!this.products) {
                console.error('Product is undefined or null');
                return;
            }

            console.log('Product before submission:', JSON.stringify(this.products, null, 2));

            if (this.products.isValid(this.currentLanguage.key)) {
                this.submitting = true;
                this.$emit('submit-requested', this.products);
                console.log('Submitting Product:', this.products);
            } else {
                console.log('Submit Validation Failed');
            }
        },

        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "ProductsList" });
        },
        genericValidator,
        errorValidator
    },
    watch:
    {
        postProductOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putProductOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseProducts: function()
        {
            this.resetProducts();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

    .showDropdown
    {
        display: block !important;
    }

    .input-group-append
    {
      height: fit-content;
    }

    img
    {
        width: 50%;
        height: 50%;
    }

</style> 